// 式神（作り直し：10種族×★1〜★3 の進化）。
// - 素体はバトルに出る妖（各章1種族）。倒すと確率で「仲間になりたそうにこちらを見ている」→ 仲間に（★1・式神Lv1）。
// - 式神Lv（霊玉）で育て、evo.evolveAt の Lv で★2・★3 に進化（名前・見た目が変わり、加勢の威力と回数が上がる）。重ねて上げる（凸）ではない。
// - 持っているだけで4人全員に効く（持っている効果＋★2からの常時効果）。
// - 加勢：キャラごとに1体（kits.companion）。戦いには出ないが、条件を満たすと自動でカットインして技を使う（★の数の回数まで）。
//   それとは別に、定期の加護（3手番ごとに重ねる上乗せ。耐久型が生きる）。どちらも battle の shiki:・boon: のフラグ。
import { ENEMIES } from '../data/enemies';
import type { RngHolder } from '../rng';
import { random } from '../rng';
import { CONFIG } from './config';
import { addMod, MOD_LABEL, type ModKey, type Mods } from './mods';
import type { OpResult, Profile, Resources, ShikigamiState } from './profile';
import type { CharId } from '../types';
import { setKit } from './items';

/** 加勢の種類のキー（種族のキー） */
export type CompanionAbility = string;

/** 加勢の技：条件・効果（[★1 Lv1 の値, ★3 Lv最大 の値]）・定期の加護 */
interface KaseiDef {
  name: string;
  trigger: string;
  effects: Record<string, [number, number]>;
  boon: { key: 'atk' | 'def' | 'regen'; v: [number, number] };
}

export interface ShikigamiDef {
  enemyId: string;
  /** 種族のキー（絵のファイル名 art/shiki/<key>-<★>.webp） */
  key: string;
  /** ★1（素体の名前）。表示は formName で★ごとに */
  name: string;
  /** ★1・★2・★3 の名前 */
  forms: [string, string, string];
  role: string;
  chapter: number;
  /** 持っている効果（全員に） */
  effect: ModKey;
  /** 加勢の技のキー（種族のキー） */
  companion: CompanionAbility;
  /** ★2 からの常時効果（全員に） */
  passive: ModKey;
  /** 得意（一覧の札の小さな文字） */
  role2: string;
}

const fx2 = (a: number, b: number): [number, number] => [a, b];
/** 10種族（各章のバトルに出る妖から） */
const ROSTER: { id: string; key: string; forms: [string, string, string]; effect: ModKey; passive: ModKey; role2: string; kasei: KaseiDef }[] = [
  { id: 'e001', key: 'mamedanuki', forms: ['豆狸', '化け狸', '隠神刑部'], effect: 'buildHp', passive: 'buildPower', role2: '建築',
    kasei: { name: '化け櫓', trigger: 'turn3', effects: { build: fx2(1, 3), mat: fx2(1, 3) }, boon: { key: 'def', v: fx2(0.03, 0.06) } } },
  { id: 'e002', key: 'kokappa', forms: ['子河童', '河太郎', '河伯'], effect: 'hp', passive: 'potionHeal', role2: '回復',
    kasei: { name: '清流の癒し', trigger: 'pinch', effects: { heal: fx2(0.3, 0.5) }, boon: { key: 'regen', v: fx2(0.02, 0.035) } } },
  { id: 'e023', key: 'kudagitsune', forms: ['管狐', '気狐', '天狐'], effect: 'skillPower', passive: 'agi', role2: '術・妖力',
    kasei: { name: '狐火の囁き', trigger: 'skill', effects: { skill: fx2(1, 1), surge: fx2(1, 2) }, boon: { key: 'atk', v: fx2(0.03, 0.05) } } },
  { id: 'e032', key: 'nurikabe', forms: ['ぬりかべ', '大ぬりかべ', '金剛壁'], effect: 'def', passive: 'hp', role2: '守り',
    kasei: { name: '鉄壁の構え', trigger: 'turn3', effects: { guard: fx2(1, 2), ward: fx2(1, 2) }, boon: { key: 'def', v: fx2(0.04, 0.08) } } },
  { id: 'e041', key: 'yukinko', forms: ['雪ん子', '雪童女', '雪華姫'], effect: 'def', passive: 'skillPower', role2: '足止め',
    kasei: { name: '吹雪', trigger: 'turn3', effects: { bind: fx2(1, 2), blast: fx2(0.6, 1.0) }, boon: { key: 'def', v: fx2(0.03, 0.06) } } },
  { id: 'e052', key: 'onibi', forms: ['鬼火', '業火', '焔王'], effect: 'atk', passive: 'skillPower', role2: '攻撃',
    kasei: { name: '燎原の火', trigger: 'turn3', effects: { blast: fx2(0.8, 1.4), burn: fx2(0.04, 0.08) }, boon: { key: 'atk', v: fx2(0.03, 0.06) } } },
  { id: 'e067', key: 'kyorinrin', forms: ['経凛々', '経蔵坊', '護法童子'], effect: 'hp', passive: 'def', role2: '護り',
    kasei: { name: '護法の経', trigger: 'pinch', effects: { guard: fx2(1, 2), ward: fx2(1, 2), heal: fx2(0.1, 0.2) }, boon: { key: 'regen', v: fx2(0.01, 0.025) } } },
  { id: 'e071', key: 'konohatengu', forms: ['木の葉天狗', '鞍馬天狗', '天狗大王'], effect: 'agi', passive: 'atk', role2: '移動・陣取り',
    kasei: { name: '木の葉風', trigger: 'turn3', effects: { move: fx2(1, 2), paint: fx2(1, 2) }, boon: { key: 'atk', v: fx2(0.02, 0.05) } } },
  { id: 'e081', key: 'aooni', forms: ['青鬼', '鬼武将', '鬼神'], effect: 'atk', passive: 'hp', role2: '攻撃の加護',
    kasei: { name: '鬼の怪力', trigger: 'kill', effects: { might: fx2(1, 2), power: fx2(1.2, 1.5) }, boon: { key: 'atk', v: fx2(0.04, 0.07) } } },
  { id: 'e091', key: 'nue', forms: ['鵺', '大鵺', '雷獣王'], effect: 'core', passive: 'atk', role2: '本陣攻め',
    kasei: { name: '落雷', trigger: 'turn3', effects: { blast: fx2(1.1, 1.7), quake: fx2(0.02, 0.04) }, boon: { key: 'atk', v: fx2(0.03, 0.05) } } },
];
const KASEI: Record<string, KaseiDef> = Object.fromEntries(ROSTER.map((r) => [r.id, r.kasei]));

export const SHIKIGAMI: Record<string, ShikigamiDef> = Object.fromEntries(
  ROSTER.filter((r) => ENEMIES[r.id]).map((r) => [
    r.id,
    { enemyId: r.id, key: r.key, name: r.forms[0], forms: r.forms, role: 'shiki', chapter: ENEMIES[r.id].chapter, effect: r.effect, companion: r.key, passive: r.passive, role2: r.role2 },
  ]),
);
export const SHIKIGAMI_IDS = Object.keys(SHIKIGAMI).sort((a, b) => SHIKIGAMI[a].chapter - SHIKIGAMI[b].chapter);
/** その敵が式神になれるか（10種族の素体だけ） */
export const isShikigami = (enemyId: string) => !!SHIKIGAMI[enemyId];

// ---------- 進化（★＝進化の段階。式神Lv で決まる） ----------

export const MAX_STAGE = 3;
/** 式神Lv での進化の段階（★1〜★3） */
export function stageOf(level: number): number {
  const [a, b] = CONFIG.shikigami.evo.evolveAt;
  return level >= b ? 3 : level >= a ? 2 : 1;
}
/** ★の名前 */
export const formName = (enemyId: string, star: number) => SHIKIGAMI[enemyId]?.forms[Math.max(1, Math.min(MAX_STAGE, star)) - 1] ?? ENEMIES[enemyId]?.name ?? '';
/** 次に進化する式神Lv（★3 なら null） */
export const nextEvolveLevel = (level: number): number | null => CONFIG.shikigami.evo.evolveAt.find((x) => level < x) ?? null;

// ---------- 勧誘 ----------

export interface RecruitOpts {
  fuda?: boolean;
  shrineLevel?: number;
  /** 使役率3倍（権利 recruit3x） */
  boost?: boolean;
  /** まだ仲間でない（初めての勧誘は firstRate） */
  first?: boolean;
}

/** 勧誘率の倍率（札・勧誘率3倍の掛け算。式神の社は足し算で recruitChance が足す） */
export function recruitMult(opts: RecruitOpts = {}): number {
  const c = CONFIG.shikigami;
  return (opts.fuda ? c.fudaMult : 1) * (opts.boost ? c.recruit3xMult : 1);
}

/** 素体を1体倒したときの勧誘率（まだ仲間でなければ firstRate。仲間の種族は rollRecruits が抽選しないので baseRate は古い記録の互換）。式神にならない敵は0 */
export function recruitChance(enemyId: string, opts: RecruitOpts = {}): number {
  const d = SHIKIGAMI[enemyId];
  if (!d || !ENEMIES[enemyId]) return 0;
  const e = CONFIG.shikigami.evo;
  // （ふつう＋社のLv×1%）×種族の倍率×札×勧誘率3倍
  const base = (opts.first ? e.firstRate : e.baseRate) + (opts.shrineLevel ?? 0) * CONFIG.shikigami.shrinePerLevel;
  return Math.min(1, base * (e.speciesMult[enemyId] ?? 1) * recruitMult(opts));
}

export interface RecruitRoll {
  enemyId: string;
  recruited: boolean;
  /** もう仲間だった（昔の記録だけ。今は仲間の種族は勧誘しない） */
  dup?: boolean;
}

/**
 * 倒した敵ごとに勧誘を抽選し、撃破数・仲間を更新する（profile を書き換える）。素体でない敵は何もしない。
 * まだ仲間でなければ★1・式神Lv1 で仲間に。仲間の種族は抽選しない（重複で霊玉を出すと周回で霊玉を稼げるため廃止）。使役率3倍は profile の権利 recruit3x から見る。
 */
export function rollRecruits(
  p: Profile,
  h: RngHolder,
  kills: Record<string, number>,
  opts: { fuda?: boolean } = {},
): { rolls: RecruitRoll[]; zeni: number; reidama: number } {
  const rolls: RecruitRoll[] = [];
  const ids = Object.keys(kills).sort();
  for (const id of ids) {
    if (!SHIKIGAMI[id]) continue;
    for (let i = 0; i < kills[id]; i++) {
      const st = (p.shikigami[id] ??= { star: 0, kills: 0, level: 1 });
      st.kills++;
      const first = st.star <= 0;
      if (!first) continue;
      const ro: RecruitOpts = { fuda: opts.fuda, shrineLevel: p.village?.levels?.yashiro ?? 0, boost: (p.entitlements ?? []).includes('recruit3x'), first };
      if (random(h) >= recruitChance(id, ro)) continue;
      st.star = 1;
      st.level = 1;
      rolls.push({ enemyId: id, recruited: true });
    }
  }
  return { rolls, zeni: 0, reidama: 0 };
}

// ---------- 式神Lv（霊玉） ----------

/** 式神Lvの上限（仲間なら maxLevel。★0 は 0） */
export function levelCap(star: number): number {
  return star <= 0 ? 0 : CONFIG.shikigami.maxLevel;
}
/** 記録の式神Lv（無い・壊れた値は1。上限で切る） */
export function shikiLevel(st: ShikigamiState | undefined): number {
  if (!st || st.star <= 0) return 1;
  const L = Math.floor(Number(st.level) || 1);
  return Math.max(1, Math.min(levelCap(st.star), L));
}
/** Lv L → L+1 の霊玉 */
export function levelUpCost(L: number): number {
  const c = CONFIG.shikigami;
  const t = Math.max(0, Math.min(1, (L - 1) / Math.max(1, c.maxLevel - 2)));
  return Math.round(c.levelCost.base + (c.levelCost.top - c.levelCost.base) * Math.pow(t, c.levelCost.exp));
}
/** Lv from → to の霊玉の合計 */
export function levelUpCostTotal(from: number, to: number): number {
  let sum = 0;
  for (let L = Math.max(1, from); L < to; L++) sum += levelUpCost(L);
  return sum;
}
/**
 * 「霊玉で強化」の案内：今の霊玉で式神Lv を1つ以上上げられる式神（出撃キャラの加勢を優先、無ければ★の高い・Lv の低い仲間）。上げられなければ null
 */
export function shikiUpgradeHint(p: Profile, reidama: number): { enemyId: string; level: number; to: number; cost: number } | null {
  const can = (id: string) => {
    const st = p.shikigami?.[id];
    if (!SHIKIGAMI[id] || !st || st.star <= 0) return null;
    const L = shikiLevel(st);
    const cap = levelCap(st.star);
    let to = L;
    let cost = 0;
    while (to < cap && cost + levelUpCost(to) <= reidama) cost += levelUpCost(to++);
    return to > L ? { enemyId: id, level: L, to, cost, star: st.star } : null;
  };
  const cid = companionIdOf(p);
  const pick =
    (cid ? can(cid) : null) ??
    Object.keys(p.shikigami ?? {})
      .map(can)
      .filter((x): x is NonNullable<ReturnType<typeof can>> => !!x)
      .sort((a, b) => b.star - a.star || a.level - b.level || a.enemyId.localeCompare(b.enemyId))[0];
  if (!pick) return null;
  const { star: _s, ...rest } = pick;
  return rest;
}

/** Lv の進み具合（Lv1=0 … 最大=1） */
const lvT = (level: number) => Math.max(0, Math.min(1, (Math.max(1, level) - 1) / Math.max(1, CONFIG.shikigami.maxLevel - 1)));
const lerp = (v: readonly [number, number] | undefined, level: number) => (v ? v[0] + (v[1] - v[0]) * lvT(level) : 0);
const r4 = (v: number) => Math.round(v * 10000) / 10000;

/** 持っている効果の値（Lv で伸びる） */
export const holdValue = (def: ShikigamiDef, level: number) => r4(lerp(CONFIG.shikigami.hold[def.role], level));
/** ★2 からの常時効果の値 */
export const passiveValue = (def: ShikigamiDef, level: number) => r4(lerp(CONFIG.shikigami.passive[def.role], level) * (CONFIG.shikigami.passiveMult[def.passive] ?? 1));
/** 持っている効果（進化で伸びる：★2 ×star3、★3 さらに ×star5） */
export const shikiHoldTotal = (def: ShikigamiDef, level: number, star: number) => {
  const C = CONFIG.shikigami.collection;
  return r4(holdValue(def, level) * C.hold * (star >= 2 ? C.star3 : 1) * (star >= 3 ? C.star5 : 1));
};
/** 常時効果（★2 から。★3 で passive4 倍） */
export const shikiPassiveTotal = (def: ShikigamiDef, level: number, star: number) => {
  const C = CONFIG.shikigami.collection;
  return star < 2 ? 0 : r4(passiveValue(def, level) * (star >= 3 ? C.passive4 : C.passive));
};

/**
 * 霊玉で式神Lvを上げる。levels 段（足りなければ 'reidama'）か、toMax なら霊玉の続く限り上限まで。上限なら 'max_level'。
 * 進化の Lv を越えたら★が上がる（evolved に新しい★）。霊玉は課金から増えない
 */
export function levelUpShikigami(
  p: Profile,
  res: Resources,
  enemyId: string,
  opts: { levels?: number; toMax?: boolean },
): OpResult<{ enemyId: string; from: number; to: number; cost: number; cap: number; evolved?: number }> {
  const def = SHIKIGAMI[enemyId];
  if (typeof enemyId !== 'string' || !def) return { ok: false, error: 'unknown_shikigami' };
  const st = p.shikigami?.[enemyId];
  if (!st || st.star <= 0) return { ok: false, error: 'not_owned' };
  const cap = levelCap(st.star);
  const from = shikiLevel(st);
  if (from >= cap) return { ok: false, error: 'max_level' };
  let to = from;
  let cost = 0;
  if (opts.toMax) {
    while (to < cap && cost + levelUpCost(to) <= res.reidama) cost += levelUpCost(to++);
  } else {
    const n = Math.floor(Number(opts.levels) || 0);
    if (n < 1) return { ok: false, error: 'levels' };
    to = Math.min(cap, from + n);
    cost = levelUpCostTotal(from, to);
    if (cost > res.reidama) return { ok: false, error: 'reidama' };
  }
  if (to <= from) return { ok: false, error: 'reidama' };
  res.reidama -= cost;
  st.level = to;
  const before = st.star;
  st.star = Math.max(st.star, stageOf(to));
  return { ok: true, enemyId, from, to, cost, cap, ...(st.star > before ? { evolved: st.star } : {}) };
}

// ---------- 持っているだけで効く効果 ----------

/** 上限をかける前の効果ごとの合計（持っている効果＋★2からの常時効果。4人に同じだけ効く） */
export function shikigamiRawMods(p: Profile, _charId?: CharId): Mods {
  const sum: Mods = {};
  for (const [id, st] of Object.entries(p.shikigami ?? {})) {
    const def = SHIKIGAMI[id];
    if (!def || st.star <= 0) continue;
    const L = shikiLevel(st);
    addMod(sum, def.effect, shikiHoldTotal(def, L, st.star));
    if (st.star >= 2) addMod(sum, def.passive, shikiPassiveTotal(def, L, st.star));
  }
  return sum;
}

/** 持っている式神の効果の合計（効果ごとに上限）。被ダメ（taken）は軽減なので負の値で返す */
export function shikigamiMods(p: Profile, charId?: CharId): Mods {
  const sum = shikigamiRawMods(p, charId);
  const out: Mods = {};
  for (const k of Object.keys(sum) as ModKey[]) {
    const v = r4(Math.min(sum[k]!, CONFIG.shikigami.cap[k] ?? 0.1));
    out[k] = k === 'taken' ? -v : v;
  }
  return out;
}

/** 仲間の式神の数 */
export const shikigamiCount = (p: Profile) => Object.entries(p.shikigami ?? {}).filter(([id, s]) => SHIKIGAMI[id] && s.star > 0).length;

const pc = (v: number) => `${Math.round(v * 1000) / 10}%`;
const SHORT_LABEL: Partial<Record<ModKey, string>> = { core: '本陣与ダメ', neutral: '中立与ダメ', boss: 'ボス与ダメ', buildHp: '建物HP', buildPower: '建物の効果', skillPower: '術の威力', potionHeal: '回復薬の回復' };
const fx = (k: ModKey, v: number) => (k === 'taken' ? `被ダメ-${pc(v)}` : `${SHORT_LABEL[k] ?? MOD_LABEL[k] ?? k}+${pc(v)}`);
/** 効果の短い文（例「HP+1.2%」） */
export const shikiFxText = fx;
/** ★2 の常時効果の説明 */
export const passiveText = (def: ShikigamiDef, level: number) => fx(def.passive, passiveValue(def, level));

// ---------- 加勢（カットインの技）・定期の加護 ----------

/** 加勢の値（★1 Lv1 → ★3 Lv最大 を、段階と Lv で） */
function kaseiValue(v: [number, number], level: number, star: number, int: boolean) {
  const t = Math.max(0, Math.min(1, ((Math.max(1, star) - 1) / (MAX_STAGE - 1)) * 0.7 + lvT(level) * 0.3));
  const x = v[0] + (v[1] - v[0]) * t;
  return int ? Math.max(1, Math.round(x)) : r4(x);
}
const INT_EFFECTS = new Set(['build', 'mat', 'guard', 'ward', 'might', 'move', 'paint', 'bind', 'seal', 'surge', 'skill']);
/** 加勢の回数（★の数） */
export const kaseiUses = (star: number) => Math.max(1, Math.min(MAX_STAGE, star));

/** 加勢の条件の説明 */
export const OUGI_TRIGGER_TEXT: Record<string, string> = {
  start: '最初の手番', mid: '中盤の手番', turn3: '3手番ごと', pinch: 'HPが半分を下回ったとき', surge3: '妖力が参になったとき',
  boss: 'ボスに攻撃したとき', core: '敵本陣に攻撃したとき', kill: '相手を倒したとき', revive: '復活したとき', skill: '術を使ったとき', build: '建てたとき',
};
const EFFECT_TEXT: Record<string, (v: number) => string> = {
  build: (v) => `周りに陣材なしで建物を${v}つ`,
  mat: (v) => `陣材+${v}`,
  heal: (v) => `HPを${pc(v)}回復`,
  skill: () => '術の待ちが明ける',
  surge: (v) => `妖力の撃破ポイント+${v}`,
  guard: (v) => `大きな攻撃を${v}回防ぐ`,
  ward: (v) => `${v}手番、被ダメ-40%`,
  bind: (v) => `敵将を${v}手番の足止め`,
  blast: (v) => `周りの敵に攻撃の${pc(v)}`,
  burn: (v) => `敵将に最大HPの${pc(v)}の火傷`,
  move: (v) => `この手番の移動+${v}`,
  paint: (v) => `周り${v}マスを自陣に`,
  might: (v) => `${v}手番、与ダメ+35%`,
  power: (v) => `次の攻撃が${Math.round(v * 100) / 100}倍`,
  quake: (v) => `敵本陣に最大HPの${pc(v)}`,
  repair: (v) => `自分の建物を${pc(v)}直す`,
};
const BOON_TEXT: Record<string, (v: number) => string> = {
  atk: (v) => `与ダメ+${pc(v)}`,
  def: (v) => `被ダメ-${pc(v)}`,
  regen: (v) => `毎手番HP${pc(v)}回復`,
};

/** 加勢の技の名前・条件・効果（式神Lv・★での値） */
export function kaseiInfo(enemyId: string, level: number, star: number) {
  const k = KASEI[enemyId];
  if (!k) return null;
  const vals = Object.entries(k.effects).map(([e, v]) => [e, kaseiValue(v, level, star, INT_EFFECTS.has(e))] as const);
  const boonV = kaseiValue(k.boon.v, level, star, false);
  const boonMax = Math.max(1, Math.min(MAX_STAGE, star)) + 1;
  return {
    name: k.name,
    trigger: k.trigger,
    when: OUGI_TRIGGER_TEXT[k.trigger] ?? k.trigger,
    uses: kaseiUses(star),
    effects: vals,
    desc: vals.map(([e, v]) => EFFECT_TEXT[e]?.(v) ?? `${e}+${v}`).join('・'),
    boon: { key: k.boon.key, value: boonV, every: 3, max: boonMax, desc: `3手番ごとに${BOON_TEXT[k.boon.key](boonV)}（${boonMax}回まで重なる）` },
  };
}

/** 式神（Lv・★）を加勢にしたときのフラグ（battle の shiki:・boon:） */
export function companionKit(enemyId: string, level: number, star: number): { mods: Mods; flags: string[] } {
  const k = kaseiInfo(enemyId, level, star);
  if (!k || star <= 0) return { mods: {}, flags: [] };
  return {
    mods: {},
    flags: [...k.effects.map(([e, v]) => `shiki:${k.trigger}:${e}=${v}`), `shiki:${k.trigger}:uses=${k.uses}`, `boon:${k.boon.key}=${k.boon.value}:${k.boon.every}:${k.boon.max}`],
  };
}

/** 戦闘のイベント companion の ability → 浮き文字の短い名前（スキルの comp:* と、式神の加勢・加護） */
export const COMPANION_EVENT_LABEL: Record<string, string> = {
  mizube: '水辺で回復',
  kasa: '攻撃を防いだ',
  kuda: '妖力+1',
  kamaitachi: '移動+1',
  hitotsume: '罠を見破った',
  relay: '龍脈で回復',
  hangon: '早く復活',
  surge: '妖力',
  slow: '足止め',
  cd: '術の待ち-1',
  mirror: '反撃',
  rise: '踏みとどまった',
  burn: '火傷',
  feast: '喰らって回復',
  ougi: '召喚',
  boon_atk: '加護・攻',
  boon_def: '加護・守',
  boon_regen: '加護・癒',
};

/** 式神の★ごとの段階（詳細の表） */
export interface ShikiSkill {
  star: number;
  kind: 'companion' | 'passive' | 'second' | 'upgrade' | 'ougi';
  label: string;
  name: string;
  desc: string;
  /** 条件（加勢だけ） */
  when?: string;
  unlocked: boolean;
}

/** 進化の段階ごとの効果（式神Lv level・★ star での値と、届いているか） */
export function shikiSkills(enemyId: string, level: number, star: number): ShikiSkill[] {
  const def = SHIKIGAMI[enemyId];
  if (!def) return [];
  const s = (st: number, kind: ShikiSkill['kind'], label: string, name: string, desc: string, when?: string): ShikiSkill => ({ star: st, kind, label, name, desc, ...(when ? { when } : {}), unlocked: star >= st });
  const k1 = kaseiInfo(enemyId, level, 1)!;
  const k2 = kaseiInfo(enemyId, level, 2)!;
  const k3 = kaseiInfo(enemyId, level, 3)!;
  return [
    s(1, 'companion', '持っている効果', def.forms[0], `全員に ${fx(def.effect, shikiHoldTotal(def, level, 1))}`),
    s(1, 'ougi', '式神召喚', k1.name, `${k1.desc}（1戦に${k1.uses}回）`, k1.when),
    s(2, 'second', `★2 ${def.forms[1]}`, '進化', `効果 ${fx(def.effect, shikiHoldTotal(def, level, 2))}・${fx(def.passive, shikiPassiveTotal(def, level, 2))}・召喚の技が強く（1戦に${k2.uses}回）`),
    s(3, 'upgrade', `★3 ${def.forms[2]}`, '最終進化', `効果 ${fx(def.effect, shikiHoldTotal(def, level, 3))}・${fx(def.passive, shikiPassiveTotal(def, level, 3))}・召喚 ${k3.desc}（1戦に${k3.uses}回）`),
    s(1, 'passive', '定期の加護', '加護', k1.boon.desc),
  ];
}

// ---------- 加勢（キャラごとに1体） ----------

export interface CompanionInfo {
  enemyId: string;
  name: string;
  star: number;
  /** 式神Lv */
  level: number;
  ability: CompanionAbility;
}

/** そのキャラの加勢の式神の id（キャラごと。kits に無いキャラは旧の共通設定）。charId を省くと出撃キャラ */
export const companionIdOf = (p: Profile, charId: CharId = p.oshi): string | null => {
  const k = p.kits?.[charId];
  return k ? k.companion : (p.companion ?? null);
};

/** そのキャラの加勢（仲間でなければ null）。charId を省くと出撃キャラ */
export function companionOf(p: Profile, charId: CharId = p.oshi): CompanionInfo | null {
  const id = companionIdOf(p, charId);
  if (!id) return null;
  const d = SHIKIGAMI[id];
  const st = p.shikigami?.[id];
  const star = st?.star ?? 0;
  if (!d || star <= 0) return null;
  return { enemyId: id, name: formName(id, star), star: Math.min(MAX_STAGE, star), level: shikiLevel(st), ability: d.companion };
}

/** そのキャラの加勢の効果（loadout のフラグ） */
export function companionEffects(p: Profile, charId: CharId = p.oshi): { mods: Mods; flags: string[]; info: CompanionInfo | null } {
  const info = companionOf(p, charId);
  if (!info) return { mods: {}, flags: [], info };
  const k = companionKit(info.enemyId, info.level, info.star);
  return { mods: k.mods, flags: k.flags, info };
}

/** そのキャラの加勢を決める（null で外す）。仲間の式神だけ。charId を省くと出撃キャラ */
export function setCompanion(p: Profile, enemyId: string | null, charId: CharId = p.oshi): OpResult<{ companion: string | null }> {
  if (enemyId === null) {
    setKit(p, charId, { companion: null });
    return { ok: true, companion: null };
  }
  if (typeof enemyId !== 'string' || !SHIKIGAMI[enemyId]) return { ok: false, error: 'unknown_shikigami' };
  if ((p.shikigami?.[enemyId]?.star ?? 0) <= 0) return { ok: false, error: 'not_owned' };
  setKit(p, charId, { companion: enemyId });
  return { ok: true, companion: enemyId };
}
